import { ActionItemViewModel, RichContentComposerResult } from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Button } from '../../../components-v2/Button';
import { ActionItemEditorFields } from './ActionItemEditorFields';
import { styleSheet } from './styles';
import { useActionItemEditor } from './useActionItemEditor';

interface IProps {
	actionItem: ActionItemViewModel;
	onCancel: () => void;
	onSaved: (vm: ActionItemViewModel, result: RichContentComposerResult) => void;
	onDeleted?: (vm: ActionItemViewModel) => void;
	onDeleteRequested?: (performDelete: () => void) => void;
	autoFocus?: boolean;
}

/**
 * Flyout-shaped action-item editor built natively from the shared {@link useActionItemEditor}
 * controller and {@link ActionItemEditorFields} — no embedded modal, no CSS overrides. Renders a
 * single scrolling body plus one footer with Save + Cancel together (and Delete for existing
 * items), which is what the embedded modal couldn't offer.
 */
export const ActionItemFlyoutEditor: React.FC<IProps> = observer(
	({ actionItem, onCancel, onSaved, onDeleted, onDeleteRequested, autoFocus }) => {
		const controller = useActionItemEditor(actionItem, { onSaved, onDeleted });
		const busy = actionItem.isBusy;

		return (
			<div className={css(styleSheet.editor)}>
				<div className={css(styleSheet.body)}>
					<ActionItemEditorFields controller={controller} actionItem={actionItem} autoFocus={autoFocus} />
				</div>

				<footer className={css(styleSheet.footer)}>
					<Button variant='primary' disabled={busy} onClick={() => controller.save()}>
						Save
					</Button>
					<Button variant='outline' disabled={busy} onClick={onCancel}>
						Cancel
					</Button>
					{actionItem.id && onDeleteRequested ? (
						<Button
							variant='ghost'
							styles={[styleSheet.deleteButton]}
							disabled={busy}
							onClick={() => onDeleteRequested(() => controller.remove())}
						>
							Delete
						</Button>
					) : null}
				</footer>
			</div>
		);
	}
);
